import {ReactNode} from 'react'

import {BadgeColor} from '@/components/common/badges/Badge'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {TextFontWeight} from '@/components/common/content/Text'
import {
  Case_Bool_Exp,
  CaseInfringementEvidenceForUserFragment,
  CaseInfringementForUserFragment,
  Trademark_Tracking,
} from '@/utils/graphqlSdk'

import {BrandSubscriptionPlatform} from '../brand-subscription/constants'
import {CaseStatus} from '../case/constants'
import {CaseSolutionItemType} from '../case-solution/constants'
import {TrademarkType} from '../constants'
import {Jurisdiction} from '../countries/constants'
import {Currency} from '../currency'

export type TrademarkTrackingFragment = Pick<Trademark_Tracking, 'name' | 'created_at'>

export enum TrademarkCardType {
  trademark = 'trademark',
  trademarkRequest = 'trademark-request',
}

export enum MessageVariant {
  info = 'info',
  success = 'success',
  warning = 'warning',
  error = 'error',
}

export enum LogoEntityType {
  RegistrationRequest = 'registration-request',
  Trademark = 'trademark',
  ExternalTrademark = 'external-trademark',
}

export type TrademarkCardContent = {
  id: string
  type: TrademarkCardType
  tmType: TrademarkType
  markName?: string | null
  markAlias?: string | null
  jurisdiction: string
  status?: string | null
  createdAt: string
  updatedAt: string
  trademarkNumber: string
}

export type CardContentButtonType = {
  text: string
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: ReactNode
  linkHref?: string
  textColor?: string
  fontWeight?: TextFontWeight
  isFullWidth?: boolean
  isHoverable?: boolean
  openInNewTab?: boolean
  onClick?: () => void
  trackedEvent?: {name: string; parameters?: Record<string, string>}
  isDisabled?: boolean
}

export enum RegistrationDetailTab {
  verification = 'verification',
  result = 'result',
  order = 'order',
  application = 'application',
  documents = 'documents',
}

export enum TrademarkDetailTab {
  general = 'general',
  owner = 'owner',
  representative = 'representative',
  monitoring = 'monitoring',
  documents = 'documents',
  classes = 'classes',
  relatedInfo = 'related-info',
  officeActions = 'office-actions',
}

export enum ServiceDetailTab {
  general = 'general',
  trademarkDetails = 'trademark-details',
  newOwnerDetails = 'new-owner-details',
  billingDetails = 'billing-details',
  documents = 'documents',
}

export enum BillingHistoryTab {
  invoices = 'invoices',
  creditNotes = 'credit-notes',
}

export type TabContent = {
  id?: string
  title: string
  content: ReactNode
  unseenCount?: number
  isDisabled?: boolean
}

export type OrderDocument = {
  id: number
  document_hub_id: string
  created_at: string
  path: string
  type: string
  revision: number
  parent_document_id?: number | null
}

export type BrandRelatedEntity =
  | 'trademark'
  | 'trademark_request'
  | 'service_request'
  | 'registration_request'

export enum TrademarkDetailActionType {
  extendClass = 'extend-class',
  extendCountry = 'extend-country',
}

export type UserBillingHistoryInvoiceProduct = {
  brand_subscription_payment?: {
    brand_subscription: {
      payment_frequency: string
      brand: {name: string}
    }
  } | null
  service_request?: {
    id: string
    service_name?: string | null
    internal_label?: string | null
  } | null
  registration_request?: {id: string} | null
}

export type UserBillingHistoryInvoice = {
  id: string
  payment_date?: string | null
  total: number | null
  currency: Currency
  product: UserBillingHistoryInvoiceProduct
}

export type UserBillingHistoryCreditNote = {
  id: number
  invoice_reference: string | null
  created_at: string
  total: number | null
  currency: Currency
}

export enum SidebarMenuGroup {
  main = 'main',
  additional = 'additional',
}

export enum SidebarMenuTab {
  trademarks = 'trademarks',
  orders = 'orders',
  brands = 'brands',
  monitoring = 'monitoring',
  cases = 'cases',
  reportThreat = 'report-threat',
  services = 'services',
  dashboard = 'dashboard',
}

export type SidebarMenuItemType = Record<
  SidebarMenuTab,
  {icon: ReactNode; title: string; linkHref: string; relatedRoutes?: string[]}
>

export enum AlertType {
  trademark = 'trademark',
  unassignedItem = 'unassignedItem',
}

export type TrademarkAlertType = {
  id: string
  title: string
  type: TrademarkType
  markName: string | null | undefined
  jurisdictions: Jurisdiction[] | string[]
  dateDescription: string
  button?: {text: string; linkHref: string}
  logoEntityType?: LogoEntityType
  logoEntityId?: string
  trademarkId: string
  alertType: AlertType.trademark
}

export type UnassignedItemsAlertType = {
  id: string
  title: string
  button: {text: string}
  alertType: AlertType.unassignedItem
}

export enum MonitoringResultTab {
  found = 'found',
  dismissed = 'dismissed',
}

// 'Closed' cases with 'Kickoff' action
export const closedCasesForUserGraphqlExp: Case_Bool_Exp = {
  status: {_eq: CaseStatus.closed},
  tracking_actions: {name: {_eq: CaseStatus.kickOffSent}},
}

// only include 'Closed' cases with 'Kickoff' action
export const foundCasesForUserGraphqlExp: Case_Bool_Exp = {
  _or: [
    {status: {_in: [CaseStatus.kickOffSent, CaseStatus.inProgress]}},
    closedCasesForUserGraphqlExp,
  ],
}

// only include 'Closed' cases with 'Kickoff' action
export const visibleCasesForUserGraphqlExp: Case_Bool_Exp = {
  _or: [
    {status: {_in: [CaseStatus.kickOffSent, CaseStatus.inProgress, CaseStatus.dismissed]}},
    closedCasesForUserGraphqlExp,
  ],
}

export type CaseInfringementsForUser = CaseInfringementForUserFragment & {
  evidences: CaseInfringementEvidenceForUserFragment[]
}

export type CaseSolutionItemForUser = {
  type: CaseSolutionItemType
  quantity: number
  service_fee: number
  government_fee?: number | null
  platform?: BrandSubscriptionPlatform
  jurisdiction?: Jurisdiction
  option?: string
  deadline?: string
}

export type CaseSolutionServiceForUser = {
  id: string
  type: string
  service_request_id?: string | null | undefined
  status: string
  parameters: {platform?: BrandSubscriptionPlatform; jurisdiction?: Jurisdiction; option?: string}
  last_status_update: string
  created_at: string
}

export enum CaseTab {
  open = 'open',
  closed = 'closed',
}

export enum MonitoringSummaryType {
  recentCases = 'recent-cases',
  allCases = 'all-cases',
  monitoredBrands = 'monitored-brands',
}

export type StatusBadge = {title: string; color: BadgeColor}

export enum ProfileTab {
  PersonalInfo = 'personal-info',
  BillingEntityInfo = 'billing-info',
  RegistrationEntityInfo = 'applicant-details',
  Payment = 'payment',
  Subscriptions = 'subscriptions',
  EmailPreferences = 'email-preferences',
  Whitelist = 'whitelist',
}

export enum PaymentCardBrand {
  visa = 'visa',
  mastercard = 'mastercard',
  amex = 'amex',
}
